import React, { useState, useEffect } from 'react';
import styles from './ReponsesTab.module.scss';
import { DEBUG, PATH } from '../../../utils/constants';

import Modal from '../Modal/Modal';

import MarkdownRenderer from '../../../utils/MarkdownRenderer/MarkdownRenderer';

import { getAllRepQcmByQuestion, insertNewReponse, updateReponse, deleteReponse } from '../../../utils/api/reponsesQcmAPI'; // Import de la fonction API
import { getAllRepLibresByQuestion, createRepLibre, updateReponse as updateReponseLibre, deleteReponse as deleteReponseLibre } from '../../../utils/api/reponsesLibresAPI'; // Import de la fonction API


const path = PATH;
const debug = DEBUG;


function ReponsesTab({ question, onClose }) {

    const [reponses, setReponses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [reponseData, setReponseData] = useState({
        texte: '',
        estCorrect: false,
        txt: '',
        img: '',
        content_type: 'texte',
        is_true: false // Type booleen
    });

    const [reponseLibreId, setReponseLibreId] = useState(null);

    useEffect(() => {
        const fetchReponses = async () => {
            try {
                setLoading(true);
                if (question.type === 'qcm') {
                    const reponsesData = await getAllRepQcmByQuestion(question.id);
                    console.log("Réponses chargées:", reponsesData);

                    if (!Array.isArray(reponsesData)) {
                        // console.warn("L'API a retourné une réponse non valide, remplacement par un tableau vide.");
                        reponsesData = [];
                    }

                    // Mapper les données de l'API aux propriétés attendues
                    const updatedReponses = reponsesData.map(reponse => ({
                        id: reponse[0],
                        img: reponse[2] ? `${path}${reponse[2]}` : '',
                        content_type: reponse[2] ? 'image' : 'texte',
                        txt: reponse[1] || '',
                        is_true: Boolean(parseInt(reponse[3], 10)), // Parsé en booleen pour correspondre au schéma de données
                        state: "unchanged"
                    }));

                    // console.log("Réponses mappées:", updatedReponses);
                    setReponses(updatedReponses);
                } else {

                    const reponse = await getAllRepLibresByQuestion(question.id);
                    const possibilities = JSON.parse(reponse?.possibilities || '[]');

                    setReponseLibreId(reponse?.id || null); // <-- Stocke l'ID ici

                    const updatedReponses = possibilities.map((texte, index) => ({
                        id: `libre-${index}`, // ID temporaire
                        texte,
                        state: "unchanged"
                    }));

                    setReponses(updatedReponses);
                }
                
                setLoading(false);
            } catch (err) {
                // console.error("Erreur lors du chargement des réponses:", err);
                // setError("Impossible de charger les réponses. Veuillez réessayer.");
                setLoading(false);
            }
        };

        fetchReponses();
    }, [question.id, question.type]);

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (name === "content_type") {
            setReponseData({
                ...reponseData,
                content_type: value,
                txt: '',
                img: ''
            });
        } else if (type === "file") {
            if (files && files[0]) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setReponseData({
                        ...reponseData,
                        img: event.target.result
                    });
                };
                reader.readAsDataURL(files[0]);
            }
        } else if (type === "checkbox") {
            // Correction ici - Utilisation directe de checked pour les checkboxes
            setReponseData({
                ...reponseData,
                [name]: checked
            });
        } else {
            setReponseData({
                ...reponseData,
                [name]: value
            });
        }
    };

    const handleAddReponse = (e) => {
        e.preventDefault();
    
        if (question.type === 'qcm' && reponses.filter(reponse => reponse.state !== "deleted").length >= 4) {
            alert("Vous ne pouvez pas ajouter plus de 4 réponses pour un QCM.");
            return;
        }
    
        if (question.type === 'qcm') {
            if (reponseData.content_type === 'texte' && !reponseData.txt) {
                alert("Veuillez saisir un texte pour cette réponse.");
                return;
            }
            if (reponseData.content_type === 'image' && !reponseData.img) {
                alert("Veuillez sélectionner une image pour cette réponse.");
                return;
            }
            
            // Générer un ID temporaire unique pour la nouvelle réponse
            const tempId = -Math.floor(Math.random() * 100000) - 1;
            
            const newReponse = {
                id: tempId,
                txt: reponseData.content_type === 'texte' ? reponseData.txt : null,
                img: reponseData.content_type === 'image' ? reponseData.img : null,
                content_type: reponseData.content_type,
                is_true: reponseData.is_true,
                state: "new"
            };
            
            // Garder une copie des réponses actuelles (y compris celles marquées comme supprimées)
            let updatedReponses = [...reponses];
            
            // Vérification pour une seule réponse correcte
            if (reponseData.is_true) {
                // Si la nouvelle réponse est correcte, vérifier qu'il n'y en a pas d'autres
                const existingCorrectAnswers = reponses.filter(r => 
                    r.state !== "deleted" && r.is_true
                );
                
                if (existingCorrectAnswers.length > 0) {
                    const confirmMultipleCorrect = window.confirm(
                        "Une autre réponse est déjà marquée comme correcte. Voulez-vous que cette nouvelle réponse devienne la seule correcte ?"
                    );
                    
                    if (confirmMultipleCorrect) {
                        // Marquer toutes les autres réponses comme incorrectes (sauf celles supprimées)
                        updatedReponses = reponses.map(r => {
                            if (r.state === "deleted") return r; // Ne pas modifier les réponses supprimées
                            return {
                                ...r,
                                is_true: false,
                                state: r.state === "new" ? "new" : "updated"
                            };
                        });
                    } else {
                        // L'utilisateur ne veut pas remplacer la réponse correcte existante
                        newReponse.is_true = false;
                    }
                }
            }
            
            // Ajouter la nouvelle réponse
            updatedReponses.push(newReponse);
            setReponses(updatedReponses);
        } else {
            // Cas des réponses libres
            const tempId = -Math.floor(Math.random() * 100000) - 1;
            const newReponse = {
                id: tempId,
                texte: reponseData.texte,
                estCorrect: true,
                state: "new"
            };
            setReponses([...reponses, newReponse]);
        }
        
        setHasUnsavedChanges(true);
        
        // Réinitialiser le formulaire
        setReponseData({
            texte: '',
            txt: '',
            img: '',
            content_type: 'texte',
            is_true: false
        });
    };

    const handleDeleteReponse = (id) => {
        setReponses(reponses.map(reponse =>
            reponse.id === id ? { ...reponse, state: "deleted" } : reponse
        ));
        setHasUnsavedChanges(true);
    };

    const handleSetCorrect = (id) => {
        if (question.type === 'qcm') {
            setReponses(reponses.map(reponse => {
                // Vérifier si l'état `is_true` change
                const isBecomingCorrect = reponse.id === id && !reponse.is_true;
                const isBecomingIncorrect = reponse.id !== id && reponse.is_true;
    
                return {
                    ...reponse,
                    is_true: reponse.id === id,
                    state: reponse.state === "new" 
                        ? "new" 
                        : (isBecomingCorrect || isBecomingIncorrect ? "updated" : reponse.state)
                };
            }));
            setHasUnsavedChanges(true);
        }
    };

    const handleSaveChanges = async () => {
        try {
            console.log('Sauvegarde des réponses:', reponses);

            // --- Si QCM ---
            if (question.type === 'qcm') {
                
                // Vérifier qu'au moins une réponse (non supprimée) est marquée comme correcte
                const correctAnswers = reponses.filter(reponse => 
                    reponse.state !== "deleted" && reponse.is_true
                );
                
                // if (!hasCorrectAnswer) {
                //     alert("Veuillez définir au moins une réponse comme correcte avant d'enregistrer.");
                //     return;
                // }

                if (correctAnswers.length === 0) { // Ajouter s'il y a des réponses (non deleted)
                    alert("Veuillez définir au moins une réponse comme correcte avant d'enregistrer.");
                    return;
                }
                
                if (correctAnswers.length > 1) {
                    alert("Une seule réponse peut être correcte pour un QCM.");
                    return;
                }
                
                // Traiter chaque réponse en fonction de son état
                const promises = reponses.map(async reponse => {
                    if (reponse.state === "new") {
                        // Pour les nouvelles réponses - pas besoin d'inclure l'ID
                        const newReponseData = {
                            id_question: question.id,
                            txt: reponse.txt || null,
                            img: reponse.img || null,
                            is_true: reponse.is_true ? 1 : 0
                        };
                        // console.log("Insertion d'une nouvelle réponse:", newReponseData);
                        return await insertNewReponse(newReponseData);
                    } else if (reponse.state === "updated") {
                        // Pour les réponses modifiées
                        const updatedReponseData = {
                            id: reponse.id,
                            txt: reponse.txt || null,
                            img: reponse.img || null,
                            is_true: reponse.is_true ? 1 : 0
                        };
                        // console.log("Mise à jour de la réponse:", updatedReponseData);
                        return await updateReponse(updatedReponseData);
                    } else if (reponse.state === "deleted") {
                        // Pour les réponses supprimées
                        // console.log("Suppression de la réponse:", reponse.id);
                        return await deleteReponse(reponse.id);
                    } 
                    // Pour les réponses inchangées, ne rien faire
                    return Promise.resolve();
                });
                
                // Attendre que toutes les opérations soient terminées
                await Promise.all(promises);

            } else if (question.type === 'libre') {
                // Filtrer les réponses en fonction de leur état
                const newResponses = reponses.filter(r => r.state === "new");
                const unchangedResponses = reponses.filter(r => r.state === "unchanged");
                const updatedResponses = reponses.filter(r => r.state === "updated");
                const deletedResponses = reponses.filter(r => r.state === "deleted");

                // On reconstitue la liste finale des réponses (sans les supprimées)
                const finalResponseList = [...newResponses, ...unchangedResponses, ...updatedResponses]
                    .map(r => r.texte)
                    .filter(t => t && t.trim() !== "");

                // S'il n'y a plus aucune réponse (tout a été supprimé)
                if (finalResponseList.length === 0) {
                    await deleteReponseLibre(reponseLibreId); // Ici, deleteReponse agit sur la question elle-même
                    return;
                }

                // S'il n'y avait pas de réponse avant (aucune unchanged/updated), on insère une nouvelle ligne
                if (unchangedResponses.length === 0 && updatedResponses.length === 0 && deletedResponses.length === 0) {
                    await createRepLibre(question.id, finalResponseList);
                } else {
                    await updateReponseLibre(reponseLibreId, finalResponseList);
                }
            }
            
            // Afficher un message de succès et fermer la modale
            alert("Les réponses ont été enregistrées avec succès.");
            setHasUnsavedChanges(false);
            
            // Fermer la modale
            onClose();
            
        } catch (err) {
            console.error("Erreur lors de la sauvegarde des réponses:", err);
            // alert("Une erreur est survenue lors de l'enregistrement des réponses.");
        }
    };

    const handleCloseWithCheck = () => {
        if (hasUnsavedChanges) {
            const confirmClose = window.confirm("Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir fermer sans sauvegarder ?");
            if (confirmClose) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    if (loading) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                        <h3>Chargement des réponses...</h3>
                    </div>
                    <div className={styles.loadingIndicator}>
                        Chargement en cours...
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                    <div className={styles.modalHeader}>
                        <h3>Erreur</h3>
                        <button className={styles.closeButton} onClick={onClose}>×</button>
                    </div>
                    <div className={styles.errorMessage}>
                        {error}
                    </div>
                    <div className={styles.modalFooter}>
                        <button className={styles.closeButton} onClick={onClose}>Fermer</button>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <Modal 
            isOpen={true}
            onClose={() => handleCloseWithCheck()}
            title={`Question ${question.placement} :`}
        >
            <div className={styles.questionText}>
                <MarkdownRenderer content={question.libelle} />
            </div>
            
            <hr className={styles.hr} />

            {question.type === 'libre' ? (
                <div className={styles.formSection}>
                    <h4>Réponse(s) acceptée(s)</h4>
                    <p className={styles.helpText}>
                        Ajoutez les réponses qui seront considérées comme correctes.
                    </p>

                    <form onSubmit={handleAddReponse}>
                        <div className={styles.formField}>
                            <label>Texte de la réponse :</label>
                            <textarea
                                name="texte"
                                value={reponseData.texte}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <button type="submit" className={styles.addButton}>
                            Ajouter cette réponse
                        </button>
                    </form>
                </div>
            ) : (
                <div className={styles.formSection}>
                    <h4>Options du QCM</h4>
                    <p className={styles.helpText}>Ajoutez les différentes options et indiquez laquelle est correcte. Maximum 4 options.</p>

                    {reponses.filter(reponse => reponse.state !== "deleted").length < 4 ? (
                        <form onSubmit={handleAddReponse}>
                            <div className={styles.formField}>
                                <label>Type de contenu :</label>
                                <div className={styles.radioGroup}>
                                    <input
                                        type="radio"
                                        id="textOption"
                                        name="content_type"
                                        value="texte"
                                        checked={reponseData.content_type === 'texte'}
                                        onChange={handleChange}
                                        defaultChecked
                                    />
                                    <label className={styles.radioLabel} htmlFor="textOption">Texte</label>
                                    <input
                                        type="radio"
                                        id="imageOption"
                                        name="content_type"
                                        value="image"
                                        checked={reponseData.content_type === 'image'}
                                        onChange={handleChange}
                                    />
                                    <label className={styles.radioLabel} htmlFor="imageOption">Image</label>
                                </div>
                            </div>

                            {reponseData.content_type === 'texte' ? (
                                <div className={styles.formField}>
                                    <label>Texte de l'option :</label>
                                    <input
                                        type="text"
                                        name="txt"
                                        value={reponseData.txt}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                            ) : (
                                <div className={styles.formField}>
                                    <label>Image :</label>
                                    <input
                                        type="file"
                                        name="image_file"
                                        accept="image/*"
                                        onChange={handleChange}
                                        required
                                    />
                                    {reponseData.img && (
                                        <div className={styles.imagePreview}>
                                            <img
                                                src={reponseData.img}
                                                alt="Prévisualisation"
                                                style={{ maxWidth: '200px', maxHeight: '150px' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={styles.formField}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="is_true"
                                        checked={reponseData.is_true}
                                        onChange={handleChange}
                                    />
                                    Cette option est la réponse correcte
                                </label>
                            </div>
                            <button type="submit" className={styles.addButton}>
                                Ajouter cette option
                            </button>
                        </form>
                    ) : (
                        <p className={styles.limitWarning}>
                            Vous avez atteint la limite de 4 options pour ce QCM.
                        </p>
                    )}
                </div>
            )}

            <div className={styles.reponsesList}>
                <h4>Réponses existantes ({reponses.filter(reponse => reponse.state !== "deleted").length})</h4>
                {reponses.length > 0 ? (
                    <ul>
                        {reponses.filter(reponse => reponse.state !== "deleted").map(reponse => (
                            <li key={reponse.id}
                                className={`${styles.reponseItem} ${reponse.is_true ? styles.correctAnswer : ''}`}
                            >
                                <div className={styles.reponseContent}>
                                    {question.type === 'qcm' ? (
                                        <>
                                            {reponse.content_type === 'image' ? (
                                                <div className={styles.imageContainer}>
                                                    <img
                                                        src={reponse.img}
                                                        alt="Réponse visuelle"
                                                        className={styles.responseImage}
                                                        onError={(e) => {
                                                            console.error("Erreur de chargement de l'image:", reponse.img);
                                                            e.target.onerror = null;
                                                            e.target.src = 'path/to/placeholder.jpg'; // Chemin vers une image de remplacement
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <span><MarkdownRenderer content={reponse.txt} /></span>
                                            )}
                                            {reponse.is_true && (
                                                <span className={styles.correctBadge}>Correcte</span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span><MarkdownRenderer content={reponse.texte} /></span>
                                        </>
                                    )}
                                </div>
                                <div className={styles.reponseActions}>
                                    {question.type === 'qcm' && !reponse.is_true && (
                                        <button
                                            className={styles.correctButton}
                                            onClick={() => handleSetCorrect(reponse.id)}
                                        >
                                            Définir comme correcte
                                        </button>
                                    )}
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => handleDeleteReponse(reponse.id)}
                                    >
                                        Supprimer
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Aucune réponse n'a encore été définie pour cette question.</p>
                )}
            </div>
                

            <div className={styles.modalFooter}>
                <button className={styles.closeButton} onClick={handleCloseWithCheck}>
                    Fermer
                </button>
                <button
                    className={`${styles.saveButton} ${!hasUnsavedChanges ? styles.disabled : ''}`}
                    onClick={handleSaveChanges}
                    disabled={!hasUnsavedChanges}
                >
                    Enregistrer
                </button>
            </div>
        </Modal>
    );
}

export default ReponsesTab;
